/**
 * engineNormalJson_Merge_Slotholder.ts - Slotholder processing for EngineNormalJson
 * Pure ES Module version
 */

import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { mergeDataJsonIntoHtmlJson } from '../../base/engine/htmlJsonMergeUtil.js';
import { EngineNormalJson } from './engineNormalJson.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import {
    TYPE_SLOTHOLDER_SKIP,
    TYPE_SLOTHTML,
    META_KEY,
    isTypePrefixedKey
} from './engineNormalJson_Merge_Constants.js';
import type { TemplateOperation } from '../../../../Assembler/dist/model/templateOperation.js';

import { TemplateOperationKind } from '../../../../Assembler/dist/model/templateOperationKind.js';
/** Parameters for ProcessSlotholder */
interface SlotholderParams {
    key: string;
    keyType: string;
    keyName: string;
    value: JsonObject;
    appSite: string;
    currentTemplateName: string;
    appView: string | null;
    templateKey: string;
    operations: TemplateOperation[];
    jsonData: JsonObject;
    componentHtml: JsonObject | null;
    genComponentHtml: boolean;
    recursionDepth: number;
    contextJson: JsonObject | null;
    flowStack: string[] | null;
    configJson: JsonObject | null;
    clientMode: boolean;
}

/**
 * Process a slotholder entry in the template
 */
function ProcessSlotholder(engine: EngineNormalJson, params: SlotholderParams): { result: string } {
    const {
        keyType,
        keyName,
        value,
        appSite,
        currentTemplateName,
        appView,
        templateKey,
        operations,
        jsonData,
        componentHtml,
        genComponentHtml,
        recursionDepth,
        contextJson,
        flowStack,
        configJson,
        clientMode
    } = params;

    // Start timing for slot processing
    const startSlot = performance.now();
    // Slotholder - merge with slot component
    const slotholderData = value;
    const slotName = slotholderData.get('SlotName').asString() || keyName; // e.g., "Center"

    // Check if skip marker is present
    const isSkipMarked = keyType === TYPE_SLOTHOLDER_SKIP ||
        (slotholderData.has('IsSkipMarked') && slotholderData.get('IsSkipMarked').asString() === 'true');

    let resultStr = '';

    if (isSkipMarked) {
        // Skip marker present - still need to get template name for operations tracking
        const slotNameWithMarker = '!' + slotName;
        Logger.flow(`ProcessSlotholder: slot=${slotName} skipMarked=true`, "NormalJson.SlotExpansion");
        const startGetSlotSkip = performance.now();
        let { htmlJson: skipHtmlJson, resolvedTemplateName: resolvedSlotName, resolvedAppSite: resolvedSlotAppSite } = engine._loader!.getTemplateWithView(appSite, currentTemplateName, slotNameWithMarker, appView, flowStack, configJson, clientMode);
        engine._getTemplateNanos += EngineNormalJson.msToNanos(performance.now() - startGetSlotSkip);
        engine._getTemplateCount++;
        Logger.flow(`ProcessSlotholder: slot=${slotName} resolved=${resolvedSlotName == null ? 'null' : resolvedSlotName} resolvedSize=${skipHtmlJson ? skipHtmlJson.size : 0}`, "NormalJson.SlotExpansion");

        const slotKey = `${resolvedSlotAppSite}_${resolvedSlotName || slotName}`;
        // Skip JsonMerge timing for skip-marked slots to match Normal engine behavior


        // For skip-marked slotholders, output DefaultSkipHtml directly
        // This matches C# behavior - skip templates don't have content to extract
        const skipHtml = engine._loader!.defaultSkipHtml;
        resultStr = skipHtml;

        // Add componentHtml entry for skip-marked slots
        // Use slotNameWithMarker to preserve ! skip marker in component output
        if (genComponentHtml && componentHtml) {
            componentHtml.set(slotNameWithMarker, skipHtml);
        }

        // Skip entire slot block (wrapper and content) - no processing
        const childOperations: TemplateOperation[] = [];

        // Process each slot content section to track operations (but discard output)
        for (const slotContentKey of slotholderData.keys()) {
            if (slotContentKey === 'SlotName' || slotContentKey === 'IsSkipMarked' || slotContentKey === META_KEY)
                continue;

            // Skip SlotHtml entries (whitespace between markers)
            const slotContentType = isTypePrefixedKey(slotContentKey) && slotContentKey.length > 0 ? slotContentKey[0] : '\0';
            if (slotContentType === TYPE_SLOTHTML)
                continue;

            const slotContentValue = slotholderData.get(slotContentKey);
            const slotContentObj = slotContentValue.asObject();
            if (slotContentObj) {
                // Process for operations tracking but don't append to result
                engine.mergeTemplate(slotContentObj, appSite, currentTemplateName, appView, templateKey, childOperations, jsonData, componentHtml, flowStack, genComponentHtml, contextJson, configJson, recursionDepth + 1, null, null, null, null, clientMode);
            }
        }

        // Add operation tracking for skip with child operations
        operations.push({
            templateName: templateKey,
            kind: TemplateOperationKind.SlotSkip,
            target: '{{!#' + slotName + '}}',
            replacedWith: slotKey,
            operations: childOperations
        } as TemplateOperation);
    } else {
        // Create immutable copy of flow stack with new item
        const newFlowStack = [...(flowStack || []), slotName];

        Logger.flow(`ProcessSlotholder: slot=${slotName} skipMarked=false`, "NormalJson.SlotExpansion");
        // Load slot component HtmlJson using loader
        const startGetSlot = performance.now();
        let { htmlJson: slotHtmlJson, dataJson: slotDataJson, resolvedTemplateName: resolvedSlotName, resolvedAppSite: resolvedSlotAppSite } = engine._loader!.getTemplateWithView(appSite, currentTemplateName, slotName, appView, newFlowStack, configJson, clientMode);
        engine._getTemplateNanos += EngineNormalJson.msToNanos(performance.now() - startGetSlot);
        engine._getTemplateCount++;
        Logger.flow(`ProcessSlotholder: slot=${slotName} resolved=${resolvedSlotName == null ? 'null' : resolvedSlotName} resolvedSize=${slotHtmlJson ? slotHtmlJson.size : 0}`, "NormalJson.SlotExpansion");

        // Update newFlowStack with resolved template name for correct inheritance lookup
        if (resolvedSlotName && resolvedSlotName !== slotName && newFlowStack.length > 0) {
            newFlowStack[newFlowStack.length - 1] = resolvedSlotName;
        }

        if (slotHtmlJson) {
            // Create child operations list for this slot
            const childOperations: TemplateOperation[] = [];

            // Use resolved template name for operations tracking
            const slotKey = `${resolvedSlotAppSite}_${resolvedSlotName || slotName}`;

            // Apply dynamic view handlers BEFORE any merging - uses getViewJson when available (no conversion overhead)
            // Skip for missing templates
            const isSlotMissing = resolvedSlotName === engine._loader!.defaultSkipTemplateName || resolvedSlotName === engine._loader!.defaultReplaceTemplateName;

            // Pre-order: Set empty entry BEFORE recursion if genComponentHtml is enabled
            // Use slotName (placeholder name) for consistency with C# EngineNormal
            if (genComponentHtml && componentHtml) {
                componentHtml.set(slotName, '');
            }

            let merged: string;
            // Only do JsonMerge and recursive processing for non-missing templates (matches Normal engine)
            if (!isSlotMissing) {
                // DynamicView handlers applied after getTemplate above (Area 7: explicit engine call)

                // Merge slot's DataJson into HtmlJson using utility (follows architecture)
                const slotJsonToMerge = slotDataJson ?? new JsonObject();
                const startSlotMerge = performance.now();
                const mergedSlotHtmlJson = mergeDataJsonIntoHtmlJson(slotHtmlJson!, slotJsonToMerge, slotKey, resolvedSlotName || slotName, childOperations, engine._loader!.defaultReplaceHtml, false, contextJson);
                engine._jsonMergeNanos += EngineNormalJson.msToNanos(performance.now() - startSlotMerge);
                engine._jsonMergeCount++;

                // Merge the slot component with the slot content (JsonKey entries already replaced)
                // Pass currentTemplateName as slotContentSourceTemplate for reuse pattern detection
                // Pass recursionDepth (not +1) since mergeSlotComponent will increment when calling mergeTemplate
                merged = engine.mergeSlotComponent(
                    mergedSlotHtmlJson,
                    slotholderData,
                    appSite,
                    slotName,
                    appView,
                    slotKey,
                    childOperations,
                    slotDataJson ? new JsonObject(slotDataJson) : jsonData,
                    currentTemplateName,
                    componentHtml,
                    newFlowStack,
                    genComponentHtml,
                    contextJson,
                    configJson,
                    recursionDepth,
                    clientMode
                );
            } else {
                // Skipped/missing slot template - traverse HtmlJson entries via mergeTemplate
                merged = engine.mergeTemplate(
                    slotHtmlJson,
                    appSite,
                    resolvedSlotName || slotName,
                    appView,
                    slotKey,
                    childOperations,
                    jsonData,
                    componentHtml,
                    newFlowStack,
                    genComponentHtml,
                    contextJson,
                    configJson,
                    recursionDepth + 1,
                    null,
                    null,
                    null,
                    null,
                    clientMode
                );
            }

            resultStr = merged;

            // Post-order: Update entry AFTER recursion if genComponentHtml is enabled
            // Use slotName (placeholder name) for consistency with C# EngineNormal
            if (genComponentHtml && componentHtml) {
                componentHtml.set(slotName, merged);
            }

            // Determine operation type based on resolvedSlotName
            let opType: TemplateOperationKind = TemplateOperationKind.SlotReplace;
            if (resolvedSlotName === engine._loader!.defaultSkipTemplateName) {
                opType = TemplateOperationKind.SlotSkip;
            } else if (resolvedSlotName === engine._loader!.defaultReplaceTemplateName) {
                opType = TemplateOperationKind.SlotMissing;
            }

            // Add operation tracking
            operations.push({
                templateName: templateKey,
                kind: opType,
                target: '{{#' + slotName + '}}',
                replacedWith: slotKey,
                operations: childOperations
            } as TemplateOperation);
        } else {
            // Slot component not found - append slot content as-is
            resultStr = `{{#${slotName}}}`;

            // Process slot content
            for (const slotContentKey of slotholderData.keys()) {
                if (slotContentKey === 'SlotName' || slotContentKey === 'IsSkipMarked' || slotContentKey === META_KEY)
                    continue;

                // Handle SlotHtml entries (whitespace between markers)
                const slotContentType = isTypePrefixedKey(slotContentKey) && slotContentKey.length > 0 ? slotContentKey[0] : '\0';
                if (slotContentType === TYPE_SLOTHTML) {
                    resultStr += slotholderData.get(slotContentKey).asString() ?? '';
                    continue;
                }

                const slotContent = slotholderData.get(slotContentKey);
                const slotContentName = GenerateHtmlJson.getKeyName(slotContentKey) || slotContentKey;
                resultStr += `{{@${slotContentName}}}`;
                const slotContentObj = slotContent.asObject();
                if (slotContentObj) {
                    resultStr += engine.mergeTemplate(slotContentObj, appSite, currentTemplateName, appView, templateKey, [], jsonData, componentHtml, newFlowStack, genComponentHtml, contextJson, configJson, recursionDepth + 1, null, null, null, null, clientMode);
                }
                resultStr += `{{/${slotContentName}}}`;
            }

            resultStr += `{{/${slotName}}}`;
        }

    }

    // Capture timing at end of slot processing
    engine._slotProcessNanos += EngineNormalJson.msToNanos(performance.now() - startSlot);
    engine._slotProcessCount++;

    return { result: resultStr };
}

export const mergeSlotholderMethods = {
    ProcessSlotholder(this: EngineNormalJson, params: SlotholderParams): { result: string } {
        return ProcessSlotholder(this, params);
    }
};
